import { useCallback, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import type { AIOwnerBrief } from '@/types/ai'

// F-AI-53: last month's menu review, built by the backend on first open and
// cached per admin. Same shape as the daily brief (an ai_digests row), so the
// dismiss flow is the same POST /ai/digest/:id/seen.

export const menuReviewQueryKey = (userId: string) => ['ai', 'menu-review', userId] as const

export function useMenuReview(userId: string, role: string) {
  const queryClient = useQueryClient()
  const key = useMemo(() => menuReviewQueryKey(userId), [userId])
  const query = useQuery({
    queryKey: key,
    queryFn: () => apiClient.aiGetMenuReview(),
    enabled: role === 'admin',
    staleTime: 60 * 60 * 1000,
    retry: false,
  })
  const markSeen = useCallback(
    (id: string) => {
      queryClient.setQueryData<Awaited<ReturnType<typeof apiClient.aiGetMenuReview>>>(key, (prev) =>
        prev?.data && prev.data.id === id ? { ...prev, data: { ...prev.data, seen: true } } : prev,
      )
      apiClient.aiMarkOwnerBriefSeen(id).catch(() => {})
    },
    [queryClient, key],
  )
  return { review: (query.data?.data ?? null) as AIOwnerBrief | null, isLoading: role === 'admin' && query.isLoading, markSeen }
}
